import { useQueries } from '@tanstack/react-query';

import { get } from '../../../redux/api';
import type { QueryKey } from '@tanstack/react-query';
import type { ProjectGroup } from '../../../../shared/model-typings/projectgroup';

//  The cached value is only the title string, so the key says so rather than reading like the whole group.
export const getReleaseGroupTitleQueryKey = (id: string): QueryKey => ['release-groups', id, 'title'];

export type ReleaseGroupLookup = {
  titles: Map<string, string>;
  //  Ids the server rejected as not found or not a valid id.
  unavailable: string[];
  //  Some lookup failed for another reason, so its id says nothing about availability.
  hasError: boolean;
  //  Every lookup has either answered or failed.
  settled: boolean;
};

/**
 * Looks up release group titles by id, for labeling a selection restored from the URL.
 */
// One request per selected group, and the project group route eager-loads every release with its projects and latest scan.
// Fine for a handful of groups; if many large groups get slow, add an id filter to GET /api/v2/release-groups
// and fetch the titles in one request.
export function useReleaseGroupsByIds(ids: string[]): ReleaseGroupLookup {
  const queries = useQueries({
    queries: ids.map((id) => ({
      queryKey: getReleaseGroupTitleQueryKey(id),
      //  `get` rejects with the Response. A 404 (deleted or not viewable) or 400 (not a valid id) is cached as `null`
      //  so it reads as an answer, not a failure.
      queryFn: async () => {
        try {
          return (await get<ProjectGroup>(`/api/project_group/${encodeURIComponent(id)}`)).data?.title || null;
        } catch (error) {
          if (typeof error === 'object' && error !== null && 'status' in error && [400, 404].includes(Number(error.status))) {
            return null;
          }
          throw error;
        }
      },
      staleTime: 1000 * 60,
      retry: false,
    })),
  });

  const lookup: ReleaseGroupLookup = { titles: new Map(), unavailable: [], hasError: false, settled: true };
  ids.forEach((id, index) => {
    const query = queries[index];
    if (query?.data) {
      lookup.titles.set(id, query.data);
    } else if (query?.data === null) {
      lookup.unavailable.push(id);
    } else if (query?.isError) {
      lookup.hasError = true;
    } else {
      lookup.settled = false;
    }
  });
  return lookup;
}
